/**
 * HomeRightPanelSlot
 * Self-contained component rendered by DashboardLayout when on /home route.
 * Reads URL state directly, renders filter/search panel content.
 *
 * Uses useIndustryCategoryFilter to ensure categories are filtered
 * by selected industries and invalid selections are auto-pruned.
 *
 * Supports: q, sections, industry_ids, category_ids
 */
import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { RightPanelMode } from '@bright-web-skeleton/ui';
import type { HomeSectionKey } from './HomeShortcutRail';
import { HomeFilterPanelContent } from './HomeFilterPanelContent';
import { HomeSearchPanelContent } from './HomeSearchPanelContent';
import { supabase } from '../../lib/supabase';
import {
  getAllIndustries,
  type NormalizedIndustryOption,
} from '@bright-web-skeleton/discover-contract';
import { useIndustryCategoryFilter, type CategoryOption } from '@bright-web-skeleton/ui';
import { useDatasetFacets } from '../../lib/DatasetFacetsContext';

interface HomeRightPanelSlotProps {
  panel: RightPanelMode;
}

/** Valid section keys */
const VALID_SECTIONS = new Set<HomeSectionKey>(['news', 'pinned', 'following']);

function parseSections(params: URLSearchParams): HomeSectionKey[] {
  return params.getAll('sections').filter((s): s is HomeSectionKey => VALID_SECTIONS.has(s as HomeSectionKey));
}

function buildHomeUrl(params: { q?: string; sections?: HomeSectionKey[]; industry_ids?: string[]; category_ids?: string[] }): URLSearchParams {
  const urlParams = new URLSearchParams();
  if (params.q?.trim()) urlParams.set('q', params.q.trim());
  params.sections?.forEach((s) => urlParams.append('sections', s));
  params.industry_ids?.forEach((id) => urlParams.append('industry_ids', id));
  params.category_ids?.forEach((id) => urlParams.append('category_ids', id));
  return urlParams;
}

export const HomeRightPanelSlot: React.FC<HomeRightPanelSlotProps> = ({ panel }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read committed state from URL
  const committedQuery = searchParams.get('q') || '';
  const committedSections = useMemo(() => parseSections(searchParams), [searchParams]);
  const committedIndustryIds = useMemo(() => searchParams.getAll('industry_ids'), [searchParams]);
  const committedCategoryIds = useMemo(() => searchParams.getAll('category_ids'), [searchParams]);

  // Draft state for filter panel
  const [draftQuery, setDraftQuery] = useState(committedQuery);
  const [draftSections, setDraftSections] = useState<HomeSectionKey[]>(committedSections);
  const [draftIndustryIds, setDraftIndustryIds] = useState<string[]>(committedIndustryIds);
  const [draftCategoryIds, setDraftCategoryIds] = useState<string[]>(committedCategoryIds);

  // Taxonomy data
  const [allIndustries, setAllIndustries] = useState<NormalizedIndustryOption[]>([]);
  const [allCategories, setAllCategories] = useState<CategoryOption[]>([]);

  // Fetch taxonomy options on mount (categories include industry_id for dependent filtering)
  useEffect(() => {
    getAllIndustries(supabase).then(setAllIndustries);
    supabase
      .from('taxonomy_categories')
      .select('id, label, industry_id')
      .order('label')
      .then(({ data }) => {
        setAllCategories(
          (data || []).map((c: any) => ({
            id: c.id,
            label: c.label,
            industry_id: c.industry_id,
          })),
        );
      });
  }, []);

  // Scope options to dataset
  const { availableIndustryIds, availableCategoryIds } = useDatasetFacets();
  const scopedIndustries = useMemo(
    () => availableIndustryIds.size > 0 ? allIndustries.filter((i) => availableIndustryIds.has(i.id)) : allIndustries,
    [allIndustries, availableIndustryIds],
  );
  const scopedCategories = useMemo(
    () => availableCategoryIds.size > 0 ? allCategories.filter((c) => availableCategoryIds.has(c.id)) : allCategories,
    [allCategories, availableCategoryIds],
  );

  // ── Dependent filtering via shared hook ──
  const { filteredCategories } = useIndustryCategoryFilter({
    industries: scopedIndustries,
    categories: scopedCategories,
    selectedIndustryIds: draftIndustryIds,
    selectedCategoryIds: draftCategoryIds,
    onCategoryIdsPruned: setDraftCategoryIds,
  });

  // Sync draft with committed on URL change
  React.useEffect(() => { setDraftQuery(committedQuery); }, [committedQuery]);
  React.useEffect(() => { setDraftSections(committedSections); }, [committedSections]);
  React.useEffect(() => { setDraftIndustryIds(committedIndustryIds); }, [committedIndustryIds]);
  React.useEffect(() => { setDraftCategoryIds(committedCategoryIds); }, [committedCategoryIds]);

  // Derive whether committed filters exist (for CTA label)
  const hasCommittedFilters = useMemo(
    () =>
      committedQuery.trim().length > 0 ||
      committedSections.length > 0 ||
      committedIndustryIds.length > 0 ||
      committedCategoryIds.length > 0,
    [committedQuery, committedSections, committedIndustryIds, committedCategoryIds],
  );

  // Derive whether draft differs from committed (for CTA disabled state)
  const draftMatchesCommitted = useMemo(() => {
    if (draftQuery !== committedQuery) return false;
    if (draftSections.join(',') !== committedSections.join(',')) return false;
    if (draftIndustryIds.join(',') !== committedIndustryIds.join(',')) return false;
    if (draftCategoryIds.join(',') !== committedCategoryIds.join(',')) return false;
    return true;
  }, [draftQuery, draftSections, draftIndustryIds, draftCategoryIds, committedQuery, committedSections, committedIndustryIds, committedCategoryIds]);

  // Commit to URL
  const commitFilters = useCallback(
    (params: { q?: string; sections?: HomeSectionKey[]; industry_ids?: string[]; category_ids?: string[] }) => {
      const merged = {
        q: params.q !== undefined ? params.q : committedQuery,
        sections: params.sections !== undefined ? params.sections : committedSections,
        industry_ids: params.industry_ids !== undefined ? params.industry_ids : committedIndustryIds,
        category_ids: params.category_ids !== undefined ? params.category_ids : committedCategoryIds,
      };
      setSearchParams(buildHomeUrl(merged), { replace: true });
    },
    [committedQuery, committedSections, committedIndustryIds, committedCategoryIds, setSearchParams],
  );

  // Filter panel handlers
  const handleToggleDraftSection = useCallback((section: HomeSectionKey) => {
    setDraftSections((prev) =>
      prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section],
    );
  }, []);

  const handleToggleDraftIndustry = useCallback((id: string) => {
    setDraftIndustryIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  }, []);

  const handleToggleDraftCategory = useCallback((id: string) => {
    setDraftCategoryIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  }, []);

  const handleApply = useCallback(() => {
    commitFilters({ q: draftQuery, sections: draftSections, industry_ids: draftIndustryIds, category_ids: draftCategoryIds });
  }, [commitFilters, draftQuery, draftSections, draftIndustryIds, draftCategoryIds]);

  const handleClear = useCallback(() => {
    setDraftQuery(committedQuery);
    setDraftSections(committedSections);
    setDraftIndustryIds(committedIndustryIds);
    setDraftCategoryIds(committedCategoryIds);
  }, [committedQuery, committedSections, committedIndustryIds, committedCategoryIds]);

  const handleClearAll = useCallback(() => {
    setDraftQuery('');
    setDraftSections([]);
    setDraftIndustryIds([]);
    setDraftCategoryIds([]);
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  // Search panel handlers
  const handleSearchCommit = useCallback(
    (explicitQuery?: string) => {
      const q = explicitQuery ?? draftQuery;
      commitFilters({ q });
    },
    [commitFilters, draftQuery],
  );

  const handleSearchToggleSection = useCallback(
    (section: HomeSectionKey) => {
      const newSections = committedSections.includes(section)
        ? committedSections.filter((s) => s !== section)
        : [...committedSections, section];
      commitFilters({ sections: newSections });
    },
    [committedSections, commitFilters],
  );

  if (panel === 'filters') {
    return (
      <div data-filter-panel="true" className="h-full">
        <HomeFilterPanelContent
          searchQuery={draftQuery}
          onSearchQueryChange={setDraftQuery}
          activeSections={draftSections}
          onToggleSection={handleToggleDraftSection}
          industryOptions={scopedIndustries}
          activeIndustryIds={draftIndustryIds}
          onToggleIndustry={handleToggleDraftIndustry}
          categoryOptions={filteredCategories}
          activeCategoryIds={draftCategoryIds}
          onToggleCategory={handleToggleDraftCategory}
          onApply={handleApply}
          onClear={handleClear}
          onClearAll={handleClearAll}
          hasCommittedFilters={hasCommittedFilters}
          draftMatchesCommitted={draftMatchesCommitted}
        />
      </div>
    );
  }

  if (panel === 'search') {
    return (
      <div data-search-panel="true" className="h-full">
        <HomeSearchPanelContent
          searchQuery={draftQuery}
          onSearchQueryChange={setDraftQuery}
          onExecuteSearch={handleSearchCommit}
          activeSections={committedSections}
          onToggleSection={handleSearchToggleSection}
        />
      </div>
    );
  }

  return null;
};

export default HomeRightPanelSlot;
